import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Check, BookOpen, ArrowRight } from "lucide-react";
import { PublicPage, Seo } from "@/components/public/PublicChrome";
import { Button } from "@/components/ui/button";
import { BOOK, COVER_SRC, openAmazon, bmTrack, BM_EVENTS } from "@/lib/businessMastery";

// Public, anonymous-safe educational/product page for Business Mastery. Reuses the shared
// book config + real cover. The in-app protected resource page (/education/business-mastery)
// is intentionally left unchanged.
export default function BusinessMasteryPublic() {
  const navigate = useNavigate();
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    bmTrack(BM_EVENTS.view, { source: "public_resource_page", location: "public_resource_page", source_page: "business_mastery_public" }, { public: true });
  }, []);
  const buy = (loc) => openAmazon({ source: "public_resource_page", location: loc, source_page: "business_mastery_public", cta_location: loc }, { public: true });

  return (
    <PublicPage>
      <Seo title="Business Mastery by Calvin Langford" path="/business-mastery"
        description="Business Mastery by Calvin Langford — a practical business education resource on ownership, systems, scaling, and long-term growth. Featured in Fairview Systems Education & Training." />

      <section className="pt-32 pb-16 px-6" data-testid="business-mastery-public">
        <div className="max-w-5xl mx-auto grid gap-10 lg:grid-cols-[320px_1fr] lg:gap-14 lg:items-center">
          <div className="flex justify-center">
            <img src={COVER_SRC} alt="Business Mastery by Calvin Langford book cover" data-testid="bm-public-cover"
              className="w-56 sm:w-64 lg:w-full lg:max-w-[320px] h-auto object-contain rounded-xl border border-[#D4AF37]/30 bg-[#0F0F10]" />
          </div>
          <div>
            <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">Featured Business Resource</div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-2">Business Mastery</h1>
            <p className="text-[#D4AF37]/90 tracking-wide mb-1">Ownership • Systems • Scale • Legacy</p>
            <p className="text-[#A1A1AA] mb-6">By Calvin Langford</p>
            <p className="text-[#A1A1AA] leading-relaxed mb-6">{BOOK.description}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button data-testid="bm-public-buy" onClick={() => buy("hero")}
                className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-12 px-7 hover:opacity-90">
                Buy on Amazon <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" data-testid="bm-public-trial" onClick={() => navigate("/register?plan=growth")}
                className="border-[#D4AF37]/40 bg-transparent text-white hover:bg-white/5 rounded-full h-12 px-7">
                Start your free trial <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 px-6 border-t border-white/8">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-10">
          <div>
            <h2 className="font-heading text-2xl mb-4">What you'll learn</h2>
            <ul className="space-y-2.5">
              {BOOK.learn.map((l) => (
                <li key={l} className="flex items-start gap-2.5 text-[#D4D4D8]"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />{l}</li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-heading text-2xl mb-4">Who it's for</h2>
            <ul className="space-y-2.5">
              {BOOK.who.map((w) => (
                <li key={w} className="flex items-start gap-2.5 text-[#D4D4D8]"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />{w}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 px-6 border-t border-white/8">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl mb-3">How it complements Fairview Systems</h2>
          <p className="text-[#A1A1AA] leading-relaxed mb-6">
            Fairview Systems gives you the operating system to build and manage your business. Business Mastery
            deepens the thinking behind it — read a chapter, then apply it in your Fairview workflows, courses,
            and tools. The book pairs naturally with the Fairview Education &amp; Training experience.
          </p>
          <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#D4AF37]" /><span className="text-white">Business Mastery is available on Amazon.</span></div>
            <Button data-testid="bm-public-buy-bottom" onClick={() => buy("bottom")}
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-11 px-6 hover:opacity-90 whitespace-nowrap">
              Buy on Amazon <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </PublicPage>
  );
}
